import Image from "@/components/Image";
import { gdImgSrcPolicy } from "@/utils/imageUrl";
import { getDiffDays } from "@/utils/time";
import useRequest from "@/utils/useReq";
import { Skeleton } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import { round } from "lodash";
import { useContext, useMemo, useState } from "react";
import { getGrocery, getVipGoods, orderCalculate } from "../../../services";
import PayBottom from "../pay/payBottom";
import { VipDialogContext } from "../shared/configContext";
import { getPayDetailBySkus, getSkusByCode } from "../shared/utils";
import styles from "./index.module.less";

/**
 * 购买套餐详情，增加主体购买弹窗商品展示内容
 * 包含：商品卡片列表，支付二维码，左侧详细描述信息
 */
export default function PkgDetail({
  defaultSelectedPkg,
  onOrderDone,
  onTransfer,
  ...rest
} = {}) {
  const { data: goodInfo } = useRequest(getVipGoods);

  const { data: dialogConf } = useRequest(async () =>
    getGrocery("vip-add-subject")
  );

  const { rightData, certSubjects } = useContext(VipDialogContext);
  const [selectedPkg, setSelectedPkg] = useState();

  const [quantity, setQuantity] = useState(1);

  // 处理显示的卡片数据，部分配置，部分需要计算
  const { data: cardList } = useRequest(
    async () => {
      if (dialogConf && rightData) {
        const result = [];
        const goods = goodInfo?.list || [];

        for (let i = 0; i < dialogConf.goods.length; i++) {
          const item = dialogConf.goods[i];

          const skus = getSkusByCode(goods, item.goodCode);

          // 没有匹配到商品
          if (!skus?.length) {
            continue;
          }

          const obj = {
            ...item,
            skus,
          };

          const calulateInfo = await orderCalculate({
            scene: "addition",

            skus: skus.map((s) => ({
              sku_id: s.id,
              quantity: 1,
              is_vip_addition: true,
            })),
            promotion: {
              activities: [],
              user_coupon_ids: [],
            },
          });

          // 有差价时，计算实际折算天数
          if (!calulateInfo.err) {
            // 计算折算的天数
            calulateInfo.days = round(
              getDiffDays(rightData.vip_expire, Date.now()),
              2
            );
            obj.calulate = calulateInfo;
          }

          result.push(obj);
        }

        // 默认选中逻辑
        if (!selectedPkg && defaultSelectedPkg) {
          const selectCard = result.find((c) =>
            defaultSelectedPkg.includes(c.goodCode)
          );
          setSelectedPkg(selectCard);
        } else {
          setSelectedPkg(result[0]);
        }

        return result;
      }
    },
    { refreshDeps: [rightData, dialogConf, goodInfo] }
  );

  const payDetails = useMemo(() => {
    if (selectedPkg) {
      let skus = selectedPkg.skus;
      const calulate = selectedPkg.calulate;

      if (calulate) {
        // 替换sku价格
        skus = skus.map((sku) => {
          const price = calulate.skus.find((s) => s.sku_id === sku.id)?.price;

          if (price !== undefined) {
            return {
              ...sku,
              price,
            };
          }

          return {
            ...sku,
          };
        });
      }

      const details = getPayDetailBySkus(skus, quantity, {
        ...(calulate
          ? {
              startTime: new Date(),
              end: rightData.vip_expire,
              extend: {
                // 与会员周期一致
                is_vip_addition: true,
              },
            }
          : {}),
      });

      return details;
    }

    return [];
  }, [selectedPkg, quantity, rightData]);

  const onAddQuantity = () => {
    if (selectedPkg) {
      setQuantity((q) => q + 1);
    }
  };

  const onMinusQuantity = () => {
    if (selectedPkg) {
      setQuantity((q) => q - 1);
    }
  };

  return (
    <div {...rest} className={styles.details}>
      <div className={styles.container}>
        <div className={styles.left}>
          <div className={styles.title}>{dialogConf?.left?.title}</div>
          <div
            className={styles.desc}
            dangerouslySetInnerHTML={{ __html: dialogConf?.left?.desc }}
          ></div>
          <div className={styles.subTitle}>
            当前可授权主体：{certSubjects?.length}个主体
          </div>

          <div className={classNames(styles.subTitle)}>
            购买可授权主体：{quantity + certSubjects?.length}
            个主体
          </div>
        </div>
        <div className={styles.right} data-order-type="加购授权主体">
          <div className={styles.pkgs}>
            {cardList ? (
              <>
                {cardList.map((item, index) => {
                  return (
                    <GoodCard
                      active={
                        item === selectedPkg || (!selectedPkg && index === 0)
                      }
                      conf={item}
                      key={item.goodCode}
                      calc={item.calulate}
                      price={
                        item.calulate
                          ? item.calulate.actual_total_price
                          : item.skus.reduce((pre, cur) => pre + cur.price, 0)
                      }
                      onClick={() => setSelectedPkg(item)}
                      expireTime={dayjs(rightData.vip_expire).format(
                        "YYYY-MM-DD"
                      )}
                      quantity={item === selectedPkg ? quantity : 1}
                      onAdd={onAddQuantity}
                      onMinus={onMinusQuantity}
                    />
                  );
                })}
              </>
            ) : (
              <div className={styles.pkgCard}>
                <Skeleton.Avatar shape="circle" size={"large"} />
                <Skeleton active paragraph={4} />
              </div>
            )}
          </div>

          <PayBottom
            details={payDetails}
            scene="addition"
            onOrderDone={onOrderDone}
            onTransfer={onTransfer}
          />
        </div>
      </div>
    </div>
  );
}
// 商品卡片
function GoodCard({
  price,
  conf,
  active,
  expireTime,
  calc,
  onClick,
  quantity,
  onAdd,
  onMinus,
}) {
  return (
    <div
      className={classNames(styles.pkgCard, { [styles.active]: active })}
      onClick={onClick}
    >
      <Image
        width={48}
        {...gdImgSrcPolicy(conf.img[0].url, {
          displayWidth: 48,
          sharpen: 100,
          type: "png",
        })}
        alt="商品图片"
      />

      <div
        className={styles.title}
        dangerouslySetInnerHTML={{ __html: conf.name }}
      ></div>
      <div className={styles.desc}>
        购买有效期至
        <span className="color-primary">{expireTime}</span>
        {calc && `（和您的会员有效期一致）`}
      </div>

      <div className={styles.price}>
        ¥<span>{price}</span>
        {conf.isTemPkg || (calc ? `(${calc.days}天)` : "/个")}
      </div>
      <div className={styles.quantity}>
        <span
          onClick={() => {
            quantity > 1 && active && onMinus && onMinus();
          }}
          className={classNames({ [styles.disabled]: quantity <= 1 })}
        >
          -
        </span>
        <span>{quantity}</span>
        <span
          onClick={() => {
            active && onAdd && onAdd();
          }}
        >
          +
        </span>
      </div>
    </div>
  );
}
